import React, { Suspense } from 'react';
import styled from 'styled-components';
import UAEventTracking from 'modules/tracking/UAEventTracking';
import Spinner from 'modules/shared/circleSpinner';
import type { IModal } from 'modules/modal';
import ua, { modifaceTracking } from '../tracking';

const { eventCategory, eventAction } = ua;

const IntroView = React.lazy(() => import(/* webpackChunkName: "modifaceIntroView" */ '../introView'));

const SpinnerWrapper = styled.div`
    flex: 1 1 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 40px;
`;

export const modifaceVersion = 'sdk_3.4.2_makeup_3.3.4';

function loadModifaceSDK(): Promise<void> {
    return new Promise((resolve) => {
        const script = document.createElement('script');
        script.src = `/scripts/lib/modiface/${modifaceVersion}/sdk.js`;
        script.async = true;
        script.onload = (): void => resolve();
        const firstScriptTag = document.getElementsByTagName('script')[0];
        firstScriptTag.parentNode?.insertBefore(script, firstScriptTag);
    });
}

export const ModifaceModalContext = React.createContext<null | { modal: IModal }>(null);

export default function loadModiface(
    productId: number
): (label: 'splash' | 'pdpButton' | 'advisorToolbox') => Promise<void> {
    return async (label): Promise<void> => {
        const [{ default: createModal }] = await Promise.all([
            import(/* webpackChunkName: "modal" */ 'modules/modal'),
            loadModifaceSDK()
        ]);

        const modal = createModal({ layout: { header: null } });

        modal({
            open: true,
            content: (
                <ModifaceModalContext.Provider value={{ modal }}>
                    <Suspense
                        fallback={
                            <SpinnerWrapper>
                                <Spinner />
                            </SpinnerWrapper>
                        }
                    >
                        <IntroView productId={productId} modal={modal} />
                    </Suspense>
                </ModifaceModalContext.Provider>
            )
        });

        UAEventTracking({ eventCategory, eventAction: eventAction.openModifaceModal, eventLabel: label });

        modifaceTracking.openModal(label);
    };
}
